import { LastRoll, PlayerPresence, RoomMeta, Unsubscribe } from './types';

const inMemoryRooms: Record<string, Record<string, PlayerPresence>> = {};
const inMemoryListeners: Record<string, ((players: Record<string, PlayerPresence>) => void)[]> = {};
const inMemoryMetaListeners: Record<string, ((meta: RoomMeta | null) => void)[]> = {};
const lastRollCache: Record<string, Record<string, LastRoll>> = {};
const inMemoryRoomMeta: Record<string, RoomMeta> = {};

function cloneSnapshot<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
}

function notify(roomId: string) {
    const players = inMemoryRooms[roomId] ?? {};
    const listeners = inMemoryListeners[roomId] ?? [];
    const snapshot = cloneSnapshot(players);
    listeners.forEach(cb => {
        try { cb(snapshot); } catch { /* ignore listener errors */ }
    });
}

function notifyMeta(roomId: string) {
    const meta = inMemoryRoomMeta[roomId] ?? null;
    const listeners = inMemoryMetaListeners[roomId] ?? [];
    const snapshot = cloneSnapshot(meta);
    listeners.forEach(cb => {
        try { cb(snapshot); } catch { /* ignore listener errors */ }
    });
}

export function upsertInMemoryPlayer(roomId: string, playerId: string, presence: PlayerPresence) {
    inMemoryRooms[roomId] = inMemoryRooms[roomId] ?? {};
    inMemoryRooms[roomId][playerId] = presence;
    notify(roomId);
}

export function removeInMemoryPlayer(roomId: string, playerId: string) {
    if (!inMemoryRooms[roomId]) return;
    delete inMemoryRooms[roomId][playerId];
    notify(roomId);
}

export function getInMemoryPlayer(roomId: string, playerId: string): PlayerPresence | undefined {
    return inMemoryRooms[roomId]?.[playerId];
}

export function hasInMemoryRoom(roomId: string): boolean {
    return Boolean(inMemoryRoomMeta[roomId]);
}

export function getInMemoryRoomMeta(roomId: string): RoomMeta | null {
    return inMemoryRoomMeta[roomId] ?? null;
}

export function createInMemoryRoomMeta(roomId: string, meta: RoomMeta): boolean {
    if (inMemoryRoomMeta[roomId]) return false;
    inMemoryRoomMeta[roomId] = meta;
    inMemoryRooms[roomId] = inMemoryRooms[roomId] ?? {};
    notifyMeta(roomId);
    return true;
}

export function updateInMemoryRoomMeta(roomId: string, meta: RoomMeta): boolean {
    if (!inMemoryRoomMeta[roomId]) return false;
    inMemoryRoomMeta[roomId] = meta;
    notifyMeta(roomId);
    return true;
}

export function listenInMemoryRoomMeta(roomId: string, cb: (meta: RoomMeta | null) => void): Unsubscribe {
    inMemoryMetaListeners[roomId] = inMemoryMetaListeners[roomId] ?? [];
    inMemoryMetaListeners[roomId].push(cb);
    try { cb(cloneSnapshot(inMemoryRoomMeta[roomId] ?? null)); } catch { cb(inMemoryRoomMeta[roomId] ?? null); }

    return () => {
        const arr = inMemoryMetaListeners[roomId];
        if (!arr) return;
        const idx = arr.indexOf(cb);
        if (idx >= 0) arr.splice(idx, 1);
    };
}

export function listenInMemoryPlayers(roomId: string, cb: (players: Record<string, PlayerPresence>) => void): Unsubscribe {
    inMemoryListeners[roomId] = inMemoryListeners[roomId] ?? [];
    inMemoryListeners[roomId].push(cb);
    const snapshot = inMemoryRooms[roomId] ?? {};
    try { cb(cloneSnapshot(snapshot)); } catch { cb(snapshot); }

    return () => {
        const arr = inMemoryListeners[roomId];
        if (!arr) return;
        const idx = arr.indexOf(cb);
        if (idx >= 0) arr.splice(idx, 1);
    };
}

export function getCachedLastRoll(roomId: string, playerId: string): LastRoll | null {
    return lastRollCache[roomId]?.[playerId] ?? null;
}

export function setCachedLastRoll(roomId: string, playerId: string, lastRoll: LastRoll) {
    lastRollCache[roomId] = lastRollCache[roomId] ?? {};
    lastRollCache[roomId][playerId] = lastRoll;
}
